import * as React from 'react';
import { t, type MessageKey } from '@reos/i18n';
import { V4FormRow, V4Group, V4Row, V4Section, V4Segmented } from '../ux-v4.js';
import {
  createPrintSet,
  getSenderProfile,
  getTemplateHistory,
  letterPreviewUrl,
  listLeads,
  listTemplates,
  printSetFileUrl,
  saveTemplate,
  updateSenderProfile,
  type LeadDto,
  type PrintSetDto,
  type SenderProfileDto,
  type TemplateDto,
  type TemplateVersionDto,
} from '../../lib/acquisition-radar-api.js';
import { formatDate, signalLabel, templateLabel } from './labels.js';

/**
 * Krok AKV1 — Dopisy: editor sablony pro kazdy signal (text s promennymi,
 * historie verzi), profil odesilatele a tiskova sada z vybranych tipu
 * (PDF, CSV, stitky). REOS dopisy NIKDY neodesila — zadne tlacitko
 * „Odeslat" tu neni; konci se tiskovou sadou ke stazeni.
 */

const tr = (key: MessageKey, values?: Record<string, string | number>): string =>
  t(key, { locale: 'cs', ...(values !== undefined ? { values } : {}) });

function errorText(error: unknown): string {
  return error instanceof Error && error.message !== '' ? error.message : tr('webAdmin.acquisitionRadar.saveError');
}

/** Promenne sablony radaru (`dopis.texty`) — napoveda pod editorem. */
const TEMPLATE_VARIABLES = [
  '{osloveni}',
  '{kancelar}',
  '{ico}',
  '{kod_dopisu}',
  '{url_namitka}',
  '{telefon_namitka}',
  '{zdroj_udaju}',
  '{doba_uchovani_mesicu}',
] as const;

type Section = 'templates' | 'sender' | 'print';

export function LettersTab(): React.JSX.Element {
  const [open, setOpen] = React.useState<Section | null>('templates');
  const toggle = (section: Section): void => setOpen(open === section ? null : section);
  return (
    <div className="rh-v4-groups" data-testid="akv-letters">
      <p className="rh-akv-note" data-testid="akv-letters-no-send">
        {tr('webAdmin.acquisitionRadar.letters.noSendNote')}
      </p>
      <V4Section
        label={tr('webAdmin.acquisitionRadar.letters.templatesTitle')}
        open={open === 'templates'}
        onToggle={() => toggle('templates')}
        testId="akv-letters-templates-toggle"
      >
        <TemplateEditor />
      </V4Section>
      <V4Section
        label={tr('webAdmin.acquisitionRadar.letters.senderTitle')}
        open={open === 'sender'}
        onToggle={() => toggle('sender')}
        testId="akv-letters-sender-toggle"
      >
        <SenderProfileForm />
      </V4Section>
      <V4Section
        label={tr('webAdmin.acquisitionRadar.letters.printTitle')}
        open={open === 'print'}
        onToggle={() => toggle('print')}
        testId="akv-letters-print-toggle"
      >
        <PrintSetBuilder />
      </V4Section>
    </div>
  );
}

function TemplateEditor(): React.JSX.Element {
  const [templates, setTemplates] = React.useState<readonly TemplateDto[] | null>(null);
  const [code, setCode] = React.useState<string>('');
  const [text, setText] = React.useState('');
  const [name, setName] = React.useState('');
  const [history, setHistory] = React.useState<readonly TemplateVersionDto[]>([]);
  const [warnings, setWarnings] = React.useState<readonly string[]>([]);
  const [error, setError] = React.useState<string | null>(null);
  const [saved, setSaved] = React.useState(false);

  React.useEffect(() => {
    listTemplates()
      .then((list) => {
        setTemplates(list);
        const first = list[0];
        if (first !== undefined) {
          setCode(first.signalCode);
          setText(first.text);
          setName(first.name ?? '');
        }
      })
      .catch((e: unknown) => setError(errorText(e)));
  }, []);

  React.useEffect(() => {
    if (code === '') return;
    getTemplateHistory(code)
      .then(setHistory)
      .catch(() => setHistory([]));
  }, [code]);

  const pick = (next: string): void => {
    const template = templates?.find((x) => x.signalCode === next);
    setCode(next);
    setText(template?.text ?? '');
    setName(template?.name ?? '');
    setWarnings([]);
    setSaved(false);
  };

  const save = (): void => {
    setError(null);
    setSaved(false);
    saveTemplate(code, text, name.trim() === '' ? null : name.trim())
      .then((updated) => {
        setTemplates((prev) => prev?.map((x) => (x.signalCode === updated.signalCode ? updated : x)) ?? null);
        setWarnings(updated.warnings);
        setSaved(true);
        return getTemplateHistory(code).then(setHistory);
      })
      .catch((e: unknown) => setError(errorText(e)));
  };

  if (templates === null) {
    return <p className="rh-akv-hint rh-akv-detail">{error ?? tr('webAdmin.acquisitionRadar.loading')}</p>;
  }

  const current = templates.find((x) => x.signalCode === code);

  return (
    <div className="rh-akv-detail" data-testid="akv-template-editor">
      <div className="rh-akv-scroll-x">
        <V4Segmented<string>
          value={code}
          options={templates.map((x) => ({ value: x.signalCode, label: templateLabel(x.signalCode) }))}
          onChange={pick}
          label={tr('webAdmin.acquisitionRadar.letters.templatePick')}
          testId="akv-template-pick"
        />
      </div>
      <p className="rh-akv-hint">
        {current?.isDefault === true
          ? tr('webAdmin.acquisitionRadar.letters.templateDefault')
          : tr('webAdmin.acquisitionRadar.letters.templateVersion', { version: current?.version ?? 0 })}
      </p>
      <label className="rh-akv-field">
        <span>{tr('webAdmin.acquisitionRadar.letters.templateName')}</span>
        <input className="rh-input" value={name} onChange={(e) => setName(e.target.value)} data-testid="akv-template-name" />
      </label>
      <label className="rh-akv-field">
        <span>{tr('webAdmin.acquisitionRadar.letters.templateText')}</span>
        <textarea
          className="rh-input rh-akv-textarea"
          value={text}
          rows={12}
          onChange={(e) => setText(e.target.value)}
          data-testid="akv-template-text"
        />
      </label>
      <p className="rh-akv-hint">
        {tr('webAdmin.acquisitionRadar.letters.variables')} <code>{TEMPLATE_VARIABLES.join(' ')}</code>
      </p>
      {warnings.length > 0 ? (
        <ul className="rh-akv-note rh-akv-note--warn" data-testid="akv-template-warnings">
          {warnings.map((w) => (
            <li key={w}>{w}</li>
          ))}
        </ul>
      ) : null}
      {error !== null ? (
        <p className="rh-akv-note rh-akv-note--warn" role="alert">
          {error}
        </p>
      ) : null}
      {saved ? (
        <p className="rh-akv-hint" role="status" data-testid="akv-template-saved">
          {tr('webAdmin.acquisitionRadar.saved')}
        </p>
      ) : null}
      <p className="rh-akv-actions">
        <button
          type="button"
          className="rh-v4-btn rh-v4-btn--filled"
          disabled={text.trim() === ''}
          onClick={save}
          data-testid="akv-template-save"
        >
          {tr('webAdmin.acquisitionRadar.letters.templateSave')}
        </button>
      </p>
      {history.length > 0 ? (
        <V4Group label={tr('webAdmin.acquisitionRadar.letters.historyTitle')} testId="akv-template-history">
          {history.map((version) => (
            <V4Row
              key={version.version}
              title={tr('webAdmin.acquisitionRadar.letters.historyVersion', { version: version.version })}
              sub={[version.name, formatDate(version.createdAt)].filter((p): p is string => p !== null && p !== '').join(' · ')}
              right={
                <button
                  type="button"
                  className="rh-v4-btn rh-v4-btn--chip"
                  onClick={() => {
                    setText(version.text);
                    setName(version.name ?? '');
                  }}
                >
                  {tr('webAdmin.acquisitionRadar.letters.historyUse')}
                </button>
              }
            />
          ))}
        </V4Group>
      ) : null}
    </div>
  );
}

const PROFILE_FIELDS: readonly { readonly key: keyof SenderProfileDto; readonly label: MessageKey; readonly type?: string }[] = [
  { key: 'brokerName', label: 'webAdmin.acquisitionRadar.sender.brokerName' },
  { key: 'officeName', label: 'webAdmin.acquisitionRadar.sender.officeName' },
  { key: 'companyId', label: 'webAdmin.acquisitionRadar.sender.companyId' },
  { key: 'address', label: 'webAdmin.acquisitionRadar.sender.address' },
  { key: 'phone', label: 'webAdmin.acquisitionRadar.sender.phone', type: 'tel' },
  { key: 'email', label: 'webAdmin.acquisitionRadar.sender.email', type: 'email' },
  { key: 'responseUrl', label: 'webAdmin.acquisitionRadar.sender.responseUrl', type: 'url' },
  { key: 'objectionUrl', label: 'webAdmin.acquisitionRadar.sender.objectionUrl', type: 'url' },
  { key: 'objectionPhone', label: 'webAdmin.acquisitionRadar.sender.objectionPhone', type: 'tel' },
];

function SenderProfileForm(): React.JSX.Element {
  const [profile, setProfile] = React.useState<SenderProfileDto | null>(null);
  const [draft, setDraft] = React.useState<Partial<Record<keyof SenderProfileDto, string>>>({});
  const [error, setError] = React.useState<string | null>(null);
  const [saved, setSaved] = React.useState(false);

  React.useEffect(() => {
    getSenderProfile()
      .then(setProfile)
      .catch((e: unknown) => setError(errorText(e)));
  }, []);

  if (profile === null) {
    return <p className="rh-akv-hint rh-akv-detail">{error ?? tr('webAdmin.acquisitionRadar.loading')}</p>;
  }

  const value = (key: keyof SenderProfileDto): string => draft[key] ?? profile[key] ?? '';

  const save = (): void => {
    setError(null);
    setSaved(false);
    const update = Object.fromEntries(
      Object.entries(draft).map(([key, v]) => [key, v.trim() === '' ? null : v.trim()]),
    );
    updateSenderProfile(update)
      .then((next) => {
        setProfile(next);
        setDraft({});
        setSaved(true);
      })
      .catch((e: unknown) => setError(errorText(e)));
  };

  return (
    <div data-testid="akv-sender-form">
      {PROFILE_FIELDS.map((field) => (
        <V4FormRow key={field.key} label={tr(field.label)}>
          <input
            className="rh-input"
            type={field.type ?? 'text'}
            value={value(field.key)}
            onChange={(e) => setDraft((prev) => ({ ...prev, [field.key]: e.target.value }))}
            aria-label={tr(field.label)}
            data-testid={`akv-sender-${field.key}`}
          />
        </V4FormRow>
      ))}
      <div className="rh-akv-detail">
        {error !== null ? (
          <p className="rh-akv-note rh-akv-note--warn" role="alert" data-testid="akv-sender-error">
            {error}
          </p>
        ) : null}
        {saved ? (
          <p className="rh-akv-hint" role="status" data-testid="akv-sender-saved">
            {tr('webAdmin.acquisitionRadar.saved')}
          </p>
        ) : null}
        <p className="rh-akv-actions">
          <button
            type="button"
            className="rh-v4-btn rh-v4-btn--filled"
            disabled={Object.keys(draft).length === 0}
            onClick={save}
            data-testid="akv-sender-save"
          >
            {tr('webAdmin.acquisitionRadar.sender.save')}
          </button>
        </p>
      </div>
    </div>
  );
}

function PrintSetBuilder(): React.JSX.Element {
  const [leads, setLeads] = React.useState<readonly LeadDto[] | null>(null);
  const [selected, setSelected] = React.useState<ReadonlySet<number>>(new Set());
  const [windowSide, setWindowSide] = React.useState<'left' | 'right'>('left');
  const [labels, setLabels] = React.useState(true);
  const [result, setResult] = React.useState<PrintSetDto | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    // Do tiskove sady radar pusti jen tipy schvalene k dopisu (stav `dopis`).
    listLeads({ status: 'letter_approved' })
      .then((list) => {
        setLeads(list);
        setSelected(new Set(list.map((l) => l.id)));
      })
      .catch((e: unknown) => setError(errorText(e)));
  }, []);

  const toggle = (id: number): void => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const build = (): void => {
    setBusy(true);
    setError(null);
    createPrintSet({ leadIds: [...selected], windowSide, labels, limitCount: 1, limitDays: 180 })
      .then(setResult)
      .catch((e: unknown) => setError(errorText(e)))
      .finally(() => setBusy(false));
  };

  if (leads === null) {
    return <p className="rh-akv-hint rh-akv-detail">{error ?? tr('webAdmin.acquisitionRadar.loading')}</p>;
  }

  return (
    <div data-testid="akv-print-builder">
      {leads.length === 0 ? (
        <p className="rh-akv-hint rh-akv-detail" data-testid="akv-print-empty">
          {tr('webAdmin.acquisitionRadar.print.empty')}
        </p>
      ) : (
        <V4Group label={tr('webAdmin.acquisitionRadar.print.pickLabel')} testId="akv-print-leads">
          {leads.map((lead) => (
            <label key={lead.id} className="rh-v4-row rh-akv-check">
              <input
                type="checkbox"
                checked={selected.has(lead.id)}
                onChange={() => toggle(lead.id)}
                data-testid={`akv-print-pick-${String(lead.id)}`}
              />
              <span className="rh-v4-row__text">
                <span className="rh-v4-row__title">{lead.postalAddress ?? tr('webAdmin.acquisitionRadar.leads.addressUnknown')}</span>
                <span className="rh-v4-row__sub">{signalLabel(lead.signalKind)}</span>
              </span>
              <a className="rh-v4-btn rh-v4-btn--chip" href={letterPreviewUrl(lead.id)} target="_blank" rel="noreferrer">
                {tr('webAdmin.acquisitionRadar.leads.letterPreview')}
              </a>
            </label>
          ))}
        </V4Group>
      )}
      <V4FormRow label={tr('webAdmin.acquisitionRadar.print.windowSide')}>
        <V4Segmented<'left' | 'right'>
          value={windowSide}
          options={[
            { value: 'left', label: tr('webAdmin.acquisitionRadar.print.windowLeft') },
            { value: 'right', label: tr('webAdmin.acquisitionRadar.print.windowRight') },
          ]}
          onChange={setWindowSide}
          label={tr('webAdmin.acquisitionRadar.print.windowSide')}
          testId="akv-print-window"
        />
      </V4FormRow>
      <V4FormRow label={tr('webAdmin.acquisitionRadar.print.labels')}>
        <input
          type="checkbox"
          checked={labels}
          onChange={(e) => setLabels(e.target.checked)}
          aria-label={tr('webAdmin.acquisitionRadar.print.labels')}
          data-testid="akv-print-labels"
        />
      </V4FormRow>
      <div className="rh-akv-detail">
        {error !== null ? (
          <p className="rh-akv-note rh-akv-note--warn" role="alert" data-testid="akv-print-error">
            {error}
          </p>
        ) : null}
        <p className="rh-akv-actions">
          <button
            type="button"
            className="rh-v4-btn rh-v4-btn--filled"
            disabled={selected.size === 0 || busy}
            onClick={build}
            data-testid="akv-print-build"
          >
            {tr('webAdmin.acquisitionRadar.print.build', { count: selected.size })}
          </button>
        </p>
        {result !== null ? <PrintSetResult result={result} labels={labels} /> : null}
      </div>
    </div>
  );
}

function PrintSetResult({ result, labels }: { readonly result: PrintSetDto; readonly labels: boolean }): React.JSX.Element {
  const skipped = result.results.filter((r) => r.outcome === 'skipped');
  return (
    <div data-testid="akv-print-result">
      <p className="rh-akv-note">{tr('webAdmin.acquisitionRadar.print.ready', { count: result.printedCount })}</p>
      {skipped.length > 0 ? (
        <ul className="rh-akv-list" data-testid="akv-print-skipped">
          {skipped.map((r) => (
            <li key={r.leadId}>
              {tr('webAdmin.acquisitionRadar.print.skipped', { id: r.leadId, reason: r.reason ?? '' })}
            </li>
          ))}
        </ul>
      ) : null}
      {result.printedCount > 0 ? (
        <p className="rh-akv-actions">
          <a className="rh-v4-btn rh-v4-btn--tinted" href={printSetFileUrl(result.id, 'pdf')} data-testid="akv-print-pdf">
            {tr('webAdmin.acquisitionRadar.print.downloadPdf')}
          </a>
          <a className="rh-v4-btn rh-v4-btn--tinted" href={printSetFileUrl(result.id, 'csv')} data-testid="akv-print-csv">
            {tr('webAdmin.acquisitionRadar.print.downloadCsv')}
          </a>
          {labels ? (
            <a className="rh-v4-btn rh-v4-btn--tinted" href={printSetFileUrl(result.id, 'labels')} data-testid="akv-print-labels-dl">
              {tr('webAdmin.acquisitionRadar.print.downloadLabels')}
            </a>
          ) : null}
        </p>
      ) : null}
    </div>
  );
}
